import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import TemplateRegistry from './components/TemplateRegistry';
import NotFound from './components/NotFound';
import HomePage from './components/HomePage';
import Login from './components/Login';
import OfflineDetector from './components/OfflineDetector';
import { isValidClientId, getClientConfig } from './config/clientConfig';
import './App.css';

// Sets the browser tab title for whichever form is open.
//
// Every form shares one index.html, so the title has to be applied at runtime
// per client rather than baked into the HTML. Clients that configure a
// branding.pageTitle get exactly that; everyone else falls back to their own
// name, so no form is left showing the generic Proctur title. The original
// title is restored on unmount, otherwise navigating between forms inside the
// SPA would leave the previous client's title in the tab.
function useFormPageTitle(clientConfig) {
  useEffect(() => {
    if (!clientConfig) return undefined;

    const configured = clientConfig.branding && clientConfig.branding.pageTitle;
    const fallback = `${clientConfig.clientName} - Registration Form`;
    const previousTitle = document.title;

    document.title = configured || fallback;

    return () => { document.title = previousTitle; };
  }, [clientConfig]);
}

// Component to validate client ID and load correct template
function ValidatedForm() {
  const { clientId } = useParams();
  const clientConfig = isValidClientId(clientId) ? getClientConfig(clientId) : null;

  useFormPageTitle(clientConfig);

  if (clientConfig) {
    const templateType = clientConfig.templateType || 'template7';

    return <TemplateRegistry templateType={templateType} clientId={clientId} />;
  } else {
    return <Navigate to="/not-found" replace />;
  }
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <OfflineDetector />
          <Routes>
            {/* Home page - Template showcase */}
            <Route path="/" element={<HomePage />} />

            {/* Admin Login page */}
            <Route path="/login" element={<Login />} />

            {/* Route with client ID parameter */}
            <Route path="/form/:clientId" element={<ValidatedForm />} />

            {/* 404 Not Found page */}
            <Route path="/not-found" element={<NotFound />} />

            {/* Catch all - redirects to not found */}
            <Route path="*" element={<Navigate to="/not-found" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
